import type { MapDocument, MapItem, MapLink } from './renderer';

/**
 * VOSviewer JSON, and the conversion into what the renderer wants.
 *
 * The API returns exactly what `journalmap.formats.vosjson.to_json_object`
 * writes: `{"network": {"items": [...], "links": [...]}}`, items keyed by
 * `id`/`label` with a `weights` object and a `scores` object, links addressing
 * their endpoints by `source_id`/`target_id`.
 *
 * The renderer wants something flatter and cheaper to draw: one numeric
 * `weight` per item and links addressing endpoints by array index. Converting
 * here rather than in either of them keeps the API's file format and the
 * renderer's draw model independent of each other, and it is where the choice
 * of "which weight attribute sizes the circles" gets made.
 */

export interface VosJsonItem {
  id?: string | number;
  label?: string;
  sublabel?: string;
  description?: string;
  url?: string;
  x?: number;
  y?: number;
  /** 1-based, VOSviewer's convention; 0 or missing means no cluster. */
  cluster?: number | null;
  weight?: number;
  weights?: Record<string, number | null>;
  score?: number | null;
  scores?: Record<string, number | null>;
  normalized_weight?: number;
  red?: number;
  green?: number;
  blue?: number;
}

export interface VosJsonLink {
  source_id: string | number;
  target_id: string | number;
  strength?: number;
}

export interface VosJsonDocument {
  network: {
    items: VosJsonItem[];
    links: VosJsonLink[];
  };
}

/** The identity used by the links, and by the renderer's `select(id)`. */
export function vosItemId(item: VosJsonItem, index: number): string {
  if (item.id !== undefined && item.id !== null) return String(item.id);
  if (item.label) return item.label;
  return String(index);
}

function finite(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * Weight attribute names in the order journalmap wrote them, which puts the
 * analysis-specific one ("Occurrences", "Documents", "Citations") first and
 * the two derived ones ("Links", "Total link strength") after it.
 */
export function weightKeys(doc: VosJsonDocument): string[] {
  const seen: string[] = [];
  for (const item of doc.network.items) {
    if (!item.weights) continue;
    for (const key of Object.keys(item.weights)) {
      if (!seen.includes(key)) seen.push(key);
    }
  }
  return seen;
}

export function scoreKeys(doc: VosJsonDocument): string[] {
  const seen: string[] = [];
  for (const item of doc.network.items) {
    if (!item.scores) continue;
    for (const key of Object.keys(item.scores)) {
      if (!seen.includes(key)) seen.push(key);
    }
  }
  return seen;
}

/** The weight of one item under a chosen attribute, with the fallbacks the
 *  format allows: a named attribute, then the bare `weight`, then 1. */
export function weightOf(item: VosJsonItem, key: string | null): number {
  if (key !== null && item.weights) {
    const value = item.weights[key];
    if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, value);
  }
  if (typeof item.weight === 'number' && Number.isFinite(item.weight)) {
    return Math.max(0, item.weight);
  }
  return 1;
}

function scoresOf(item: VosJsonItem): Record<string, number> | undefined {
  const out: Record<string, number> = {};
  if (item.scores) {
    for (const [key, value] of Object.entries(item.scores)) {
      // A NaN score is "no value" and the renderer paints it grey; leaving the
      // key out entirely says the same thing without a NaN in the data.
      if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
    }
  }
  if (typeof item.score === 'number' && Number.isFinite(item.score) && !('score' in out)) {
    out['score'] = item.score;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Convert one VOSviewer JSON document into the renderer's `MapDocument`.
 *
 * `weightKey` chooses which weight attribute sizes the circles; pass null for
 * the first attribute the document offers. Links whose endpoints are not in
 * the item list are dropped rather than throwing, because a hand-edited file
 * with a dangling endpoint should still open.
 */
export function toMapDocument(doc: VosJsonDocument, weightKey: string | null): MapDocument {
  const source = doc.network.items;
  const position = new Map<string, number>();
  const items: MapItem[] = source.map((item, index) => {
    const id = vosItemId(item, index);
    position.set(id, index);
    const scores = scoresOf(item);
    const built: MapItem = {
      id,
      label: item.label ?? id,
      x: finite(item.x, 0),
      y: finite(item.y, 0),
      cluster: Math.round(finite(item.cluster, 0)),
      weight: weightOf(item, weightKey),
    };
    if (scores) built.scores = scores;
    if (item.url) built.url = item.url;
    if (item.description) built.description = item.description;
    if (item.sublabel) built.sublabel = item.sublabel;
    return built;
  });

  const links: MapLink[] = [];
  for (const link of doc.network.links) {
    const from = position.get(String(link.source_id));
    const to = position.get(String(link.target_id));
    if (from === undefined || to === undefined || from === to) continue;
    links.push({ source: from, target: to, strength: finite(link.strength, 1) });
  }

  return { items, links };
}

/** Look-up from item id to the original JSON item, for the detail panel. */
export function indexById(doc: VosJsonDocument): Map<string, VosJsonItem> {
  const index = new Map<string, VosJsonItem>();
  doc.network.items.forEach((item, position) => index.set(vosItemId(item, position), item));
  return index;
}
